import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../../provider";
import { Formik, Form } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import axios from "axios";
import { alpha } from "@mui/material/styles";
import {
  Grid,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Divider,
  Radio,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MapPin, Phone, Mail, FileText, Info, Calendar } from "lucide-react";
import * as c from "../../../constants";
import PageProtectedLayout from "../../../components/Page/PageProtectedLayout";
import FormAlert from "../../../components/Alert/FormAlert";
import ButtonForm from "../../../components/Button/ButtonForm";
import DateInput from "../../../components/Forms/DateInput";
import LabWorkflowSteps from "../../../components/Steps/LabWorkflowSteps";
import CustomDialogSmall from "../../../components/Dialog/DiaCompSmall";
import { useTranslation } from "../../../language";

// Lab visit states considered inactive — no fallback to these
const INACTIVE_STATES = [2, 3, 4, 5, 6];

// ── Card / row helpers — liquid glass shell, same recipe used elsewhere in
// the app (translucent fill + blur + inset highlight) ──
function cardSx(theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    border: '1px solid',
    borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)',
    borderRadius: '16px !important', // !important needed for Accordion: MUI's :first-of-type/:last-of-type defaults outrank a plain sx value
    overflow: 'hidden',
    bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
      : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
  };
}

function LabRow({ lab, selected, onSelect, onInfo }) {
  const theme = useTheme();
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        py: 0.5, px: 2, borderRadius: 4, overflow: 'hidden', cursor: 'pointer',
        border: '1px solid',
        borderColor: selected
          ? theme.palette.primary.main
          : (theme.palette.mode === 'light' ? 'grey.C900' : 'greydark.C400'),
        bgcolor: selected
          ? (theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900')
          : (theme.palette.mode === 'light' ? 'grey.C50' : 'greydark.C900'),
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Radio checked={selected} onChange={onSelect} size="small" sx={{ py: 0.5 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={400}>{lab.name}</Typography>
      </Box>
      <Tooltip title="Info" placement="top">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onInfo(); }}
          sx={{ flexShrink: 0, '&:hover': { transform: 'scale(1.2)' } }}
        >
          <Info size={18} color={theme.palette.secondary.dark} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const TIME_FORMAT = "HH:mm";

// Themed time selector — uses theme.jsx colors for the picker popup
// (a native <input type="time"> clock icon can't be recolored, MUI's TimePicker can)
function ThemedTimeInput({ label, value, onChange, error, helperText }) {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        label={label}
        ampm={false}
        value={value ? dayjs(value, TIME_FORMAT) : null}
        onChange={(newValue) =>
          onChange(newValue && newValue.isValid() ? newValue.format(TIME_FORMAT) : "")
        }
        slotProps={{
          textField: {
            fullWidth: true,
            size: "small",
            margin: "normal",
            error,
            helperText,
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.grey.C200,
                borderRadius: 6,
              },
              '& .MuiClockNumber-root': {
                color: theme.palette.greydark.C900,
                fontWeight: 600,
              },
              '& .MuiClockNumber-root.Mui-selected': {
                color: theme.palette.common.white,
              },
              '& .MuiClock-pin, & .MuiClockPointer-root': {
                backgroundColor: theme.palette.grey.C500,
              },
              '& .MuiClockPointer-thumb': {
                backgroundColor: theme.palette.grey.C500,
                borderColor: theme.palette.grey.C500,
              },
              '& .MuiDialogActions-root .MuiButton-root': {
                color: theme.palette.greydark.C900,
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

const getValidationSchema = (t) =>
  Yup.object({
    laboratory_id: Yup.number().required(t("val_required")),
    visit_date: Yup.date()
      .min(new Date(), t("val_date_past"))
      .required(t("val_required")),
    visit_time: Yup.string().required(t("val_time_req")),
  });

// ── Date utilities ───────────────────────────────────────────────────────────
const DATE_DISPLAY_FORMAT = "dddd, MMMM D, YYYY";
const DATE_API_FORMAT = "YYYY-MM-DD";

const formatDateForDisplay = (dateString) =>
  dateString ? dayjs(dateString).format(DATE_DISPLAY_FORMAT) : "";

const formatDateForAPI = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString, [DATE_DISPLAY_FORMAT, DATE_API_FORMAT], true);
  if (!date.isValid()) {
    const fallback = dayjs(dateString);
    return fallback.isValid() ? fallback.format(DATE_API_FORMAT) : "";
  }
  return date.format(DATE_API_FORMAT);
};

// Lab "notes" is free text entered per-lab in the backend and sometimes hardcodes
// both languages inline (e.g. "Blutabnahme bis / Blood draw until: 15:00 Uhr",
// or a day name like "(Samstag/Saturday)"). Collapse both patterns down to just
// the current UI language instead of always showing both stitched together.
const BLOOD_DRAW_UNTIL_RE = /Blutabnahme bis\s*\/\s*Blood draw until:?/gi;
const BILINGUAL_PAREN_RE = /\(([^/()]+)\/([^/()]+)\)/g;
const localizeLabNotes = (notes, t, language) => {
  if (!notes) return notes;
  return notes
    .replace(BLOOD_DRAW_UNTIL_RE, t("onb_lab_blood_draw_until"))
    .replace(BILINGUAL_PAREN_RE, (_match, de, en) => (language === "de" ? de.trim() : en.trim()));
};

// ── Component ────────────────────────────────────────────────────────────────
export default function StateLabAppointment() {
  const { t, language } = useTranslation();
  dayjs.locale(language);
  const theme = useTheme();

  const { labVisitId: labVisitIdFromParams } = useParams();
  const { state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/StateAddOn";

  // ── Visit state ──────────────────────────────────────────────────────────
  const [visits, setVisits] = useState([]);
  const [currentVisit, setCurrentVisit] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Form state ───────────────────────────────────────────────────────────
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const submitLock = useRef(false);

  // ── Labs state ───────────────────────────────────────────────────────────
  const [labs, setLabs] = useState([]);
  const [loadingLabs, setLoadingLabs] = useState(true);
  const [errorLabs, setErrorLabs] = useState(null);
  const [infoLab, setInfoLab] = useState(null);

  // ── Resolve active visit ID ──────────────────────────────────────────────
  // Prefer the URL param; fall back to the active visit found in the full list
  const activeVisitId = useMemo(
    () => labVisitIdFromParams ?? currentVisit?.id ?? null,
    [labVisitIdFromParams, currentVisit]
  );

  // The visit record to seed the form / display context - avoids an unnecessary "fetch all" call when the URL param is already there.
  const labVisit = useMemo(() => {
    if (labVisitIdFromParams) {
      return visits.find((v) => String(v.id) === String(labVisitIdFromParams)) ?? currentVisit;
    }
    return currentVisit;
  }, [labVisitIdFromParams, visits, currentVisit]);

  // ── 1. Fetch visits ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!state?.user?.id) return;
    let mounted = true;

    const fetchVisits = async () => {
      setPageLoading(true);
      try {
        // If we have a param ID, fetch that single record directly (fast path)
        // Otherwise fetch all and derive the active one
        if (labVisitIdFromParams) {
          const { data } = await axios.get(
            `${c.API_URL}/api/user-lab-visits/${labVisitIdFromParams}/`
          );
          if (mounted) {
            setVisits(data ? [data] : []);
            setCurrentVisit(data ?? null);
          }
        } else {
          const { data } = await axios.get(`${c.API_URL}/api/user-lab-visits/`);
          const visitsArray = Array.isArray(data) ? data : data ? [data] : [];
          const active =
            visitsArray.find((v) => !INACTIVE_STATES.includes(v.state)) ?? null;
          if (mounted) {
            setVisits(visitsArray);
            setCurrentVisit(active);
          }
        }
      } catch (err) {
        console.error(err);
        if (mounted) setError("Unable to load lab visit.");
      } finally {
        if (mounted) setPageLoading(false);
      }
    };

    fetchVisits();
    return () => { mounted = false; };
  }, [state?.user?.id, labVisitIdFromParams]);

  // ── 2. Fetch laboratory options ──────────────────────────────────────────
  useEffect(() => {
    const fetchLabs = async () => {
      try {
        const { data } = await axios.get(`${c.API_URL}/api/laboratories/`);
        setLabs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch labs:", err);
        setErrorLabs("Unable to load labs. Please try again later.");
      } finally {
        setLoadingLabs(false);
      }
    };

    fetchLabs();
  }, []);

  // ── Form submission ──────────────────────────────────────────────────────
  const handleSubmit = async (values, formikHelpers) => {
    if (submitLock.current) return;
    submitLock.current = true;

    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      setLoadingSubmit(true);
    //coerce laboratory to a string before patching
    //converts the number 7 to "7"
      const response = await axios.patch(
        `${c.API_URL}/api/user-lab-visits/${activeVisitId}/`,
        { ...values, state: 2 }
      );

      if ([200, 201].includes(response.status)) {
        setSuccessMessage("Lab visit scheduled successfully!");
        setTimeout(() => {
          navigate(from, {
            replace: true,
            state: { labVisitId: activeVisitId },
          });
        }, 1500);
      }
    } catch (err) {
      if (err.response?.data) {
        const apiErrors = err.response.data;
        Object.keys(apiErrors).forEach((field) => {
          const message = Array.isArray(apiErrors[field])
            ? apiErrors[field].join(" ")
            : apiErrors[field];
          formikHelpers.setFieldError(field, message);
        });
        setErrorMessage("Please fix the errors above.");
      } else {
        setErrorMessage("Server error. Please try again later.");
      }
    } finally {
      setLoadingSubmit(false);
      formikHelpers.setSubmitting(false);
      submitLock.current = false;
    }
  };

  // ── Guards ───────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!labVisit || !activeVisitId) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <Typography variant="subtitle1" color="text.secondary">
          No active lab visit found. Please start a new lab visit.
        </Typography>
      </Box>
    );
  }

  // ── Initial form values ──────────────────────────────────────────────────
  const initialValues = {
    laboratory_id: "",
    visit_date: "",
    visit_time: "",
  };

  const accentLeft = theme.palette.tertiary.C300;
  const accentRight = theme.palette.quartiary.C300;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <PageProtectedLayout headline={t("lab_header3")} specify={t("lab_header4")}>
      <>
        <Grid
          container
          spacing={1}
          sx={{
            maxWidth: 1000,
            mb: 16,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* ── Workflow Stepper ── */}
          <Grid
            size={12}
            sx={{
              maxWidth: 800,
              my: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LabWorkflowSteps stepNumber={0} />
          </Grid>


          {/* ── Form ── */}
          <Grid size={12} sx={{ width: "100%", maxWidth: 900, px: { xs: 1, sm: 2 } }}>
            <Formik
              initialValues={initialValues}
              validationSchema={getValidationSchema(t)}
              enableReinitialize={true}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, isSubmitting, touched, errors }) => (
                <Form>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                    {t("onb_lab_accordion_hint")}
                  </Typography>

                  {/* ── Box 1: Wähle ein Labor ── */}
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{ ...cardSx(theme, accentLeft), mb: 2, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 2.5, py: 1,
                        '& .MuiAccordionSummary-content': { display: 'flex', flexDirection: 'column', gap: 1, my: 1 },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                            border: `2px solid ${theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="number4"
                            sx={{ color: theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100, fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}
                          >
                            1
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" fontWeight={400}>{t("onb_lab_select_heading")}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                      {errorLabs ? (
                        <FormAlert severity="error" message={errorLabs} />
                      ) : loadingLabs ? (
                        <Box display="flex" justifyContent="center" py={2}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <Stack spacing={1.5}>
                          {labs.map((lab) => (
                            <LabRow
                              key={lab.id}
                              lab={lab}
                              selected={String(values.laboratory_id) === String(lab.id)}
                              onSelect={() => setFieldValue("laboratory_id", lab.id)}
                              onInfo={() => setInfoLab(lab)}
                            />
                          ))}
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* ── Box 2: Wähle Datum & Uhrzeit ── */}
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{ ...cardSx(theme, accentRight), mb: 2, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 2.5, py: 1,
                        '& .MuiAccordionSummary-content': { display: 'flex', flexDirection: 'column', gap: 1, my: 1 },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                            border: `2px solid ${theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="number4"
                            sx={{ color: theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100, fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}
                          >
                            2
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" fontWeight={400}>{t("onb_lab_datetime_heading")}</Typography>
                      </Stack>
                      <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                        <Typography component="li" variant="body2" color="text.secondary">
                          {t("onb_lab_hours_note")}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                      <DateInput label={t("onb_lab3")} name="visit_date" />
                      <ThemedTimeInput
                        label={t("onb_lab4")}
                        value={values.visit_time}
                        onChange={(newValue) => setFieldValue("visit_time", newValue)}
                        error={touched.visit_time && Boolean(errors.visit_time)}
                        helperText={touched.visit_time && errors.visit_time ? errors.visit_time : ''}
                      />
                    </AccordionDetails>
                  </Accordion>

                  {/* ── Box 3: Überprüfe deine Auswahl und bestätige ── */}
                  <Box sx={{ ...cardSx(theme, accentLeft), p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                          border: `2px solid ${theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="number4"
                          sx={{ color: theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100, fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}
                        >
                          3
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" fontWeight={400}>{t("onb_lab_review_heading")}</Typography>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.05 : 0.12),
                        borderRadius: 3,
                        p: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                            bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <MapPin size={20} color={accentLeft} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={400}>
                            {labs.find((l) => String(l.id) === String(values.laboratory_id))?.name || t("onb_lab5")}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Calendar size={16} color={accentLeft} />
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              {values.visit_date || t("onb_lab3")} · {values.visit_time || t("onb_lab4")}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {successMessage && (
                      <FormAlert severity="success" title="Success" message={successMessage} onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }} />
                    )}

                    {loadingSubmit ? (
                      <Grid container sx={{ justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                      </Grid>
                    ) : (
                      <ButtonForm
                        name={t("onb_labb")}
                        disabled={isSubmitting || loadingSubmit}
                      />
                    )}

                    {errorMessage && (
                      <FormAlert severity="error" title="Form Submission Error" message={errorMessage} onClose={() => setErrorMessage(null)} sx={{ mt: 2 }} />
                    )}
                  </Box>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>

        {infoLab && (
          <CustomDialogSmall
            open
            onClose={() => setInfoLab(null)}
            title={infoLab.name}
            content={
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <MapPin size={22} color={theme.palette.primary.main} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                      {t("onb_lab_info_address")}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                      {[
                        infoLab.details?.address?.street,
                        infoLab.details?.address?.city,
                        infoLab.details?.address?.postal_code,
                        infoLab.details?.address?.country,
                      ].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                </Box>

                {infoLab.details?.phone && (
                  <>
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Phone size={22} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                          {t("onb_lab_info_phone")}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                          {infoLab.details.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {infoLab.details?.email && (
                  <>
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Mail size={22} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                          {t("onb_lab_info_email")}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                          {infoLab.details.email}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {infoLab.details?.notes && (
                  <>
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <FileText size={22} color={theme.palette.primary.main} style={{ marginTop: 2, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                          {t("onb_lab_info_notes")}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 400, whiteSpace: 'pre-line' }}>
                          {localizeLabNotes(infoLab.details.notes, t, language)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Stack>
            }
          />
        )}
      </>
    </PageProtectedLayout>
  );
}
